(function () {
  function getNodes(scope, selector) {
    const nodes = Array.from(scope.querySelectorAll(selector));
    if (scope.matches && scope.matches(selector)) nodes.unshift(scope);
    return nodes;
  }

  /**
   * Slick/Swiper carousel slides marked aria-hidden frequently still expose
   * focusable descendants. `inert` removes them from the sequential focus
   * order (WCAG 2.4.3 Focus Order).
   */
  function inertHiddenJudgeMeSlides(scopeRoot) {
    const scope = scopeRoot || document;

    scope.querySelectorAll('.jdgm-widget .slick-slide').forEach((slide) => {
      if (slide.classList.contains('slick-cloned')) {
        slide.setAttribute('inert', '');
        return;
      }
      if (slide.getAttribute('aria-hidden') === 'true') {
        slide.setAttribute('inert', '');
      } else {
        slide.removeAttribute('inert');
      }
    });

    scope.querySelectorAll('.jdgm-widget .swiper-slide').forEach((slide) => {
      if (slide.getAttribute('aria-hidden') === 'true') {
        slide.setAttribute('inert', '');
      } else {
        slide.removeAttribute('inert');
      }
    });
  }

  /**
   * Judge.me widgets render their `header_text` setting inside an <h*>
   * (commonly <h2>) wrapper even when the setting is left blank, leaving
   * an empty heading in the DOM. Empty headings fail WCAG 1.3.1 / 2.4.6
   * (Heading Levels) — screen readers announce "heading level 2" with
   * no content, and accessibility audits flag them as errors.
   *
   * The homepage carousel deliberately ships `header_text: ""` because
   * the editorial brand header (`homepage-reviews-brand` snippet) and the
   * "Most recent reviews" text block above each carousel already provide
   * the visible heading. We can't change the app's render output, but we
   * can prune empty headings inside any Judge.me-owned container.
   */
  function removeEmptyJudgeMeHeadings(scopeRoot) {
    const scope = scopeRoot || document;
    const containers = getNodes(
      scope,
      '.jdgm-widget, [class*="jdgm-cards-carousel"], [class*="jdgm-reviews-carousel"], [class*="jdgm-rev-widg"], [class*="jdgm-section"]',
    );
    containers.forEach((container) => {
      container.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((heading) => {
        const text = (heading.textContent || '').replace(/\s|\u00A0/g, '');
        if (text === '') heading.remove();
      });
    });
  }

  /**
   * Apply native lazy-loading + async decoding to every Judge.me image
   * we can find. The widget injects review-media images eagerly which
   * costs bandwidth on first paint even when the Judge.me carousel
   * itself sits below the fold (typical homepage layout). Adding
   * `loading="lazy"` + `decoding="async"` lets the browser skip the
   * fetch + decode work until the image scrolls near the viewport.
   *
   * Lighthouse field-data on this site previously reported up to
   * ~400 KB of off-screen image bytes from the Judge.me carousel
   * widgets — this knocks that down to whatever the user actually
   * scrolls to.
   *
   * Idempotent: marks each image with `data-sk-lazy-applied` so the
   * MutationObserver loop doesn't re-touch already-processed nodes
   * on every callback.
   */
  function isHomepageReviewsBandImage(img) {
    const section = img.closest('.shopify-section');
    return Boolean(section?.querySelector('.hp-rv-brand-header'));
  }

  /** Homepage cards carousel: load images with text (no lazy flash). */
  function eagerLoadHomepageReviewCarouselImages(scopeRoot) {
    const scope = scopeRoot || document;
    const images =
      scope.nodeType === 1 && scope.matches?.('img') && isHomepageReviewsBandImage(scope)
        ? [scope]
        : getNodes(scope, '.jdgm-card.jdgm-media-card img').filter(isHomepageReviewsBandImage);

    images.forEach((img) => {
      img.loading = 'eager';
      img.setAttribute('fetchpriority', 'high');
      img.setAttribute('decoding', 'async');
      img.dataset.skLazyApplied = 'eager';
    });
  }

  function lazyLoadJudgeMeImages(scopeRoot) {
    const scope = scopeRoot || document;
    getNodes(scope, '.jdgm-widget img, [class*="jdgm-cards-carousel"] img, [class*="jdgm-rev-widg"] img').forEach((img) => {
      if (img.dataset.skLazyApplied === '1') return;
      if (isHomepageReviewsBandImage(img)) {
        eagerLoadHomepageReviewCarouselImages(img);
        return;
      }
      img.dataset.skLazyApplied = '1';
      if (!img.hasAttribute('loading')) img.setAttribute('loading', 'lazy');
      if (!img.hasAttribute('decoding')) img.setAttribute('decoding', 'async');
      /* fetchpriority="low" further deprioritises these on mobile
         where the browser fetch queue is contention-bound. */
      if (!img.hasAttribute('fetchpriority')) img.setAttribute('fetchpriority', 'low');
    });
  }

  /** Watermelon BCC PDP still uses a legacy Judge.me block until template cache clears. */
  function mountPdpReviewsBrand(scopeRoot) {
    const onBccPdp =
      document.querySelector('main[data-template="product.breast-cancer-cure"]') ||
      document.getElementById('pdp-rv-brand-source');
    if (!onBccPdp) return;

    const source = document.getElementById('pdp-rv-brand-source');
    const header = source?.querySelector('.hp-rv-brand-header');
    if (!header) return;

    const scope = scopeRoot && scopeRoot.nodeType === 1 ? scopeRoot : document;
    const sections =
      scope.matches?.('.shopify-section') && scope.querySelector('.jdgm-cards-carousel')
        ? [scope]
        : Array.from(scope.querySelectorAll?.('.shopify-section') || []).filter((section) =>
            section.querySelector('.jdgm-cards-carousel'),
          );

    sections.forEach((section) => {
      if (section.querySelector('.hp-rv-brand-header')) return;

      const mountPoint =
        section.querySelector('.section-content-wrapper') ||
        section.querySelector('.custom-section-content') ||
        section.querySelector('.section');
      if (!mountPoint) return;

      mountPoint.insertBefore(header.cloneNode(true), mountPoint.firstChild);

      section.querySelectorAll('.jdgm-header').forEach((node) => {
        node.style.setProperty('display', 'none', 'important');
      });
    });
  }

  function getHomepageReviewsSections(scopeRoot) {
    const scopeRootNode = scopeRoot && scopeRoot.nodeType === 1 ? scopeRoot : document;
    const sections = [];

    if (scopeRootNode.matches?.('.shopify-section') && scopeRootNode.querySelector('.hp-rv-brand-header')) {
      sections.push(scopeRootNode);
      return sections;
    }

    if (!scopeRootNode.querySelectorAll) return sections;

    scopeRootNode.querySelectorAll('.shopify-section').forEach((section) => {
      if (section.querySelector('.hp-rv-brand-header')) sections.push(section);
    });
    return sections;
  }

  /** Override Judge.me track width so desktop shows 3 full cards, mobile 1 + peek. */
  function layoutHomepageReviewCarousel(scope) {
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const gap = mobile ? 14 : 20;
    const gutterX = mobile ? 24 : 20;
    const trackPadX = gutterX * 2;
    const trackMax = mobile
      ? Math.min(window.innerWidth - trackPadX, 420)
      : Math.min(1152, window.innerWidth - trackPadX - 16);
    const innerTrack = Math.max(0, trackMax - trackPadX);
    const cardW = mobile
      ? Math.min(280, Math.round(innerTrack - gap - 4))
      : Math.floor((innerTrack - gap * 2) / 3);

    getHomepageReviewsSections(scope).forEach((section) => {
      const wrapper = section.querySelector('.jdgm-cards-wrapper');
      if (!wrapper) return;

      wrapper.style.setProperty('max-width', `${trackMax}px`, 'important');
      wrapper.style.setProperty('width', `${trackMax}px`, 'important');
      wrapper.style.setProperty('margin-inline', 'auto', 'important');
      wrapper.style.setProperty('padding-top', mobile ? '0.5rem' : '0.4rem', 'important');
      wrapper.style.setProperty('padding-bottom', mobile ? '0.85rem' : '0.75rem', 'important');
      wrapper.style.setProperty('padding-left', `${gutterX}px`, 'important');
      wrapper.style.setProperty('padding-right', `${gutterX}px`, 'important');

      const container = section.querySelector('.jdgm-videos-container');
      if (container) {
        container.style.setProperty('max-width', `${trackMax}px`, 'important');
      }

      section.querySelectorAll('.jdgm-card.jdgm-media-card').forEach((card) => {
        card.style.setProperty('width', `${cardW}px`, 'important');
        card.style.setProperty('min-width', `${cardW}px`, 'important');
        card.style.setProperty('max-width', `${cardW}px`, 'important');
        card.style.setProperty('flex', `0 0 ${cardW}px`, 'important');
        card.style.setProperty('margin-inline', '0', 'important');
      });
    });
  }

  /** Judge.me centres quote copy in a grid; stars/name stay left — align one inset. */
  function alignHomepageReviewCardCopy(scope) {
    getHomepageReviewsSections(scope).forEach((section) => {
      section.querySelectorAll('.jdgm-card.jdgm-media-card .jdgm-text').forEach((textEl) => {
        textEl.style.setProperty('display', 'block', 'important');
        textEl.style.setProperty('grid-template-columns', 'none', 'important');
        textEl.style.setProperty('justify-items', 'start', 'important');
        textEl.style.setProperty('align-items', 'start', 'important');
        textEl.style.setProperty('place-items', 'start', 'important');
        textEl.style.setProperty('justify-content', 'flex-start', 'important');
        textEl.style.setProperty('text-align', 'left', 'important');

        textEl.querySelectorAll('p').forEach((paragraph) => {
          paragraph.style.setProperty('width', '100%', 'important');
          paragraph.style.setProperty('max-width', 'none', 'important');
          paragraph.style.setProperty('margin-inline', '0', 'important');
          paragraph.style.setProperty('padding-inline', '0', 'important');
          paragraph.style.setProperty('text-align', 'left', 'important');
        });
      });

      section.querySelectorAll('.jdgm-card.jdgm-media-card .jdgm-rating-section').forEach((ratingEl) => {
        ratingEl.style.setProperty('align-items', 'flex-start', 'important');
        ratingEl.style.setProperty('text-align', 'left', 'important');
      });

      section.querySelectorAll('.jdgm-card.jdgm-media-card .jdgm-reviewer-info').forEach((infoEl) => {
        infoEl.style.setProperty('justify-content', 'flex-start', 'important');
        infoEl.style.setProperty('align-items', 'flex-start', 'important');
        infoEl.style.setProperty('text-align', 'left', 'important');
      });
    });
  }

  function stripHomepageReviewShadowStack(scope) {
    getHomepageReviewsSections(scope).forEach((section) => {
      section
        .querySelectorAll(
          '.jdgm-cards-wrapper, .jdgm-videos-container, .jdgm-content, .jdgm-widget.jdgm-cards-carousel',
        )
        .forEach((node) => {
          node.style.setProperty('box-shadow', 'none', 'important');
          node.style.setProperty('filter', 'none', 'important');
        });

      section.querySelectorAll('.jdgm-card.jdgm-media-card').forEach((card) => {
        if (!card.dataset.skRvShadowReset) {
          card.dataset.skRvShadowReset = '1';
          card.style.removeProperty('box-shadow');
          card.style.removeProperty('filter');
        }
      });
    });
  }

  /**
   * A11Y-001: Judge.me stamps aria-selected on plain <button>s in the media
   * grid (invalid outside tablist/listbox/grid/radiogroup). Convert the grid
   * to a radiogroup + radio pattern with aria-checked, and strip leftover
   * aria-selected elsewhere. Attribute observer below re-runs when the app
   * re-injects aria-selected after clicks / carousel updates.
   */
  function fixInvalidJudgeMeAriaSelected(scopeRoot) {
    const scope = scopeRoot || document;

    getNodes(scope, '.jm-media-grid, .jdgm-gallery').forEach((grid) => {
      const items = Array.from(
        grid.querySelectorAll(
          ':scope > .jm-media-grid__item, :scope > button, .jm-media-grid__item, button[aria-selected]',
        ),
      ).filter((el) => el.closest('.jm-media-grid, .jdgm-gallery') === grid);

      if (!items.length) return;

      if (!grid.getAttribute('role')) grid.setAttribute('role', 'radiogroup');
      if (!grid.getAttribute('aria-label')) grid.setAttribute('aria-label', 'Review media');

      items.forEach((control, index) => {
        const selected =
          control.getAttribute('aria-selected') === 'true' ||
          control.getAttribute('aria-checked') === 'true' ||
          control.getAttribute('aria-current') === 'true' ||
          control.classList.contains('jm-media-grid__item--selected') ||
          control.classList.contains('is-selected') ||
          control.classList.contains('active');

        control.removeAttribute('aria-selected');
        if (control.getAttribute('role') !== 'radio') control.setAttribute('role', 'radio');
        control.setAttribute('aria-checked', selected ? 'true' : 'false');

        if (selected) {
          control.setAttribute('aria-current', 'true');
          if (!control.hasAttribute('tabindex') || control.getAttribute('tabindex') === '-1') {
            control.setAttribute('tabindex', '0');
          }
        } else {
          control.removeAttribute('aria-current');
          control.setAttribute('tabindex', '-1');
        }

        const existing = (control.getAttribute('aria-label') || '').trim();
        if (!existing || /^go to item\b/i.test(existing)) {
          control.setAttribute('aria-label', `Review photo ${index + 1} of ${items.length}`);
        }
      });
    });

    const leftover =
      '.jm-media-grid__item[aria-selected], .jdgm-widget button[aria-selected], .jdgm-widget [role="button"][aria-selected], [class*="jdgm-"] button[aria-selected], [class*="jm-"] button[aria-selected]';

    getNodes(scope, leftover).forEach((control) => {
      const composite = control.closest('[role="tablist"], [role="listbox"], [role="grid"], [role="radiogroup"]');
      if (composite && composite.getAttribute('role') !== 'radiogroup') return;
      if (composite && composite.getAttribute('role') === 'radiogroup' && control.getAttribute('role') === 'radio') {
        const isOn = control.getAttribute('aria-selected') === 'true';
        control.removeAttribute('aria-selected');
        control.setAttribute('aria-checked', isOn ? 'true' : 'false');
        if (isOn) control.setAttribute('aria-current', 'true');
        else control.removeAttribute('aria-current');
        return;
      }

      const isCurrent = control.getAttribute('aria-selected') === 'true';
      control.removeAttribute('aria-selected');

      if (isCurrent && control.matches('.jm-media-grid__item')) {
        control.setAttribute('aria-current', 'true');
      } else if (!control.matches('.jm-media-grid__item')) {
        control.removeAttribute('aria-current');
      }
    });
  }

  /** Judge.me medal badge links use an SVG without exposing a name on the <a>. */
  function nameJudgeMeMedalLinks(scopeRoot) {
    const scope = scopeRoot || document;
    getNodes(scope, 'a.jm-medals__medal-link').forEach((link) => {
      const svg = link.querySelector('svg');
      const svgTitle =
        (svg && (svg.getAttribute('title') || (svg.querySelector('title') && svg.querySelector('title').textContent))) ||
        '';
      const label = (svgTitle || 'Judge.me shop medal').trim();
      if (!link.getAttribute('aria-label')) {
        link.setAttribute('aria-label', label);
      }
      link.setAttribute('title', link.getAttribute('title') || label);
      if (svg) {
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
      }
    });
  }

  /**
   * A11Y-021: Histogram star links often ship empty text with a generic
   * aria-label like "1 star". Enrich with purpose + count when available.
   */
  function nameJudgeMeHistogramLinks(scopeRoot) {
    const scope = scopeRoot || document;
    getNodes(
      scope,
      '.jdgm-histogram a, .jdgm-histogram__row a, .jdgm-histogram__bar-container a, [class*="jdgm-histogram"] a',
    ).forEach((link) => {
      if (link.dataset.skJmHistogramNamed === '1') return;

      const row = link.closest('.jdgm-histogram__row, .jdgm-histogram__bar, [class*="histogram"]');
      const rowText = (row?.textContent || '').replace(/\s+/g, ' ').trim();
      const starMatch =
        rowText.match(/(\d)\s*star/i) ||
        (link.getAttribute('aria-label') || '').match(/(\d)\s*star/i) ||
        (link.getAttribute('data-rating') || '').match(/(\d)/) ||
        (link.getAttribute('href') || '').match(/rating[=-](\d)/i);

      const stars = starMatch ? starMatch[1] : null;
      const countEl = row?.querySelector(
        '.jdgm-histogram__frequency, .jdgm-histogram__count, [class*="frequency"], [class*="count"]',
      );
      let count = (countEl?.textContent || '').replace(/[^\d]/g, '');
      if (!count) {
        const bare = rowText.match(/\((\d+)\)|\b(\d+)\s*$/);
        count = bare ? bare[1] || bare[2] : '';
      }

      const existing = (link.getAttribute('aria-label') || '').trim();
      const generic =
        !existing ||
        /^\d\s*stars?$/i.test(existing) ||
        (/^\d/.test(existing) && existing.length < 14 && !/review|filter|show/i.test(existing));

      if (generic && stars) {
        const starWord = stars === '1' ? 'star' : 'stars';
        const label = count
          ? `Show ${stars}-${starWord} reviews (${count})`
          : `Show ${stars}-${starWord} reviews`;
        link.setAttribute('aria-label', label);
      } else if (!existing) {
        link.setAttribute('aria-label', 'Filter reviews by star rating');
      }

      link.querySelectorAll('svg').forEach((svg) => {
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
      });
      link.dataset.skJmHistogramNamed = '1';
    });
  }

  // Cans dropped the -nz suffix; the legacy keys stay for older links and the glass SKUs.
  const US_FLAVOR_TITLE_BY_HANDLE = {
    'sidekick-cherry-soda-12-pack': 'Sidekick Cherry',
    'sidekick-cherry-soda-12-pack-nz': 'Sidekick Cherry',
    'sidekick-cherry-soda-glass-bottles-12-pack-nz': 'Sidekick Cherry',
    'sidekick-pear-soda-12-pack': 'Sidekick Pear',
    'sidekick-pear-soda-12-pack-nz': 'Sidekick Pear',
    'sidekick-pear-soda-glass-bottles-12-pack-nz': 'Sidekick Pear',
    'sidekick-strawberry-soda-12-pack': 'Sidekick Strawberry',
    'sidekick-strawberry-soda-12-pack-nz': 'Sidekick Strawberry',
    'sidekick-strawberry-soda-glass-bottles-12-pack-nz': 'Sidekick Strawberry',
    'sidekick-mango-soda-12-pack': 'Sidekick Mango',
    'sidekick-mango-soda-12-pack-nz': 'Sidekick Mango',
    'sidekick-mango-soda-glass-bottles-12-pack-nz': 'Sidekick Mango',
    'sidekick-watermelon-soda-12-pack': 'Sidekick Watermelon',
    'sidekick-watermelon-soda-12-pack-nz': 'Sidekick Watermelon',
  };

  function usFlavorTitleFromNzLabel(label) {
    const text = (label || '').trim();
    if (!text) return null;

    const handleMatch = text.match(/sidekick-[\w-]+/i);
    if (handleMatch) {
      const mapped = US_FLAVOR_TITLE_BY_HANDLE[handleMatch[0].toLowerCase()];
      if (mapped) return mapped;
    }

    if (/cherry/i.test(text)) return 'Sidekick Cherry';
    if (/pear/i.test(text)) return 'Sidekick Pear';
    if (/strawberry/i.test(text)) return 'Sidekick Strawberry';
    if (/mango/i.test(text)) return 'Sidekick Mango';
    if (/watermelon/i.test(text)) return 'Sidekick Watermelon';
    return null;
  }

  function rewriteNzPackLabel(text) {
    const current = (text || '').trim();
    if (!current) return null;
    if (!/12-pack|glass bottles|soda 12|product page|sidekick-(cherry|pear|strawberry|mango|watermelon)|-nz\b/i.test(current)) {
      return null;
    }
    const usTitle = usFlavorTitleFromNzLabel(current);
    if (!usTitle) return null;
    if (/view/i.test(current)) return `View ${usTitle} product page`;
    if (/product page/i.test(current)) return `${usTitle} product page`;
    return usTitle;
  }

  function rewriteJudgeMeUsProductReferences(scopeRoot) {
    if (!document.body.classList.contains('us-showcase')) return;

    const scope = scopeRoot || document;
    getNodes(
      scope,
      [
        '.jm-review-item button',
        '.jm-review-item a',
        '.jm-review-item [aria-label]',
        '.jdgm-rev__prod-info a',
        '.jdgm-rev__prod-info button',
        '.jdgm-rev__prod-info',
        '.jdgm-rev__prod-title',
        '.jdgm-rev__product-title',
        '.jdgm-product-title',
        '.jdgm-product-name',
        '.jdgm-product-name a',
        '.jm-product-variant-card__thumbnail',
        '.jm-product-variant-card__product-link',
        '.jm-product-variant-card button',
        '.jm-product-variant-card a',
        '.jm-product-variant-card',
        '.jdgm-widget button[aria-label]',
        '.jdgm-widget a[aria-label]',
        '.jdgm-widget [aria-label*="product page"]',
        '.jdgm-widget [aria-label*="12-Pack"]',
        '.jdgm-widget [aria-label*="Glass Bottles"]',
        '.jdgm-widget [title*="12-Pack"]',
        '.jdgm-widget [title*="Glass Bottles"]',
        '.jdgm-widget [title*="NZ"]',
      ].join(', '),
    ).forEach((control) => {
      const attrs = ['aria-label', 'title'];
      let changed = false;

      attrs.forEach((attr) => {
        const currentLabel = (control.getAttribute(attr) || '').trim();
        const nextLabel = rewriteNzPackLabel(currentLabel);
        if (nextLabel && currentLabel !== nextLabel) {
          control.setAttribute(attr, nextLabel);
          changed = true;
        }
      });

      /* Prefer rewriting leaf text nodes so we don't wipe nested markup. */
      const childCount = control.childElementCount;
      if (childCount === 0) {
        const visibleText = (control.textContent || '').trim();
        const nextText = rewriteNzPackLabel(visibleText);
        if (nextText && visibleText !== nextText) {
          control.textContent = nextText;
          changed = true;
        }
      } else {
        control.childNodes.forEach((node) => {
          if (node.nodeType !== 3) return;
          const visibleText = (node.textContent || '').trim();
          const nextText = rewriteNzPackLabel(visibleText);
          if (nextText && visibleText !== nextText) {
            node.textContent = nextText;
            changed = true;
          }
        });
      }

      if (changed) control.dataset.skUsTitleFixed = '1';
    });
  }

  function makeJudgeMeScrollRegionsKeyboardAccessible(scopeRoot) {
    const scope = scopeRoot || document;
    getNodes(
      scope,
      [
        '.jdgm-widget [style*="overflow"]',
        '.jm-media-grid',
        '.jm-carousel',
        '.jdgm-gallery',
        '.jdgm-gallery__wrapper',
        '.jdgm-rev-widg__body',
        '.jdgm-rev-widg__reviews',
        '.jdgm-rev__pics',
        '.jdgm-carousel-wrapper',
        '.jdgm-cards-wrapper',
        '[class*="jdgm-"][style*="overflow"]',
        '[class*="jm-"][style*="overflow"]',
        '[data-sk-jm-scroll-region="1"]',
      ].join(', '),
    ).forEach((region) => {
      if (!(region instanceof HTMLElement)) return;
      /* Media grids become radiogroups with their own tab stops. */
      if (region.getAttribute('role') === 'radiogroup') return;
      if (region.querySelector(':scope > [role="radio"], :scope > .jm-media-grid__item')) return;

      const style = getComputedStyle(region);
      const overflowAllowsScroll =
        style.overflowX === 'auto' ||
        style.overflowX === 'scroll' ||
        style.overflowY === 'auto' ||
        style.overflowY === 'scroll' ||
        style.overflow === 'auto' ||
        style.overflow === 'scroll';
      const scrollable =
        overflowAllowsScroll &&
        (region.scrollWidth > region.clientWidth + 2 || region.scrollHeight > region.clientHeight + 2);

      if (!scrollable) {
        if (region.dataset.skJmScrollRegion === '1') {
          region.removeAttribute('tabindex');
          if (
            region.getAttribute('role') === 'region' &&
            /^Review (media|list)$/i.test(region.getAttribute('aria-label') || '')
          ) {
            region.removeAttribute('role');
            region.removeAttribute('aria-label');
          }
          delete region.dataset.skJmScrollRegion;
        }
        return;
      }

      /* Prefer focusing the outermost scrollport when nested. */
      const parentScroll = region.parentElement?.closest('[data-sk-jm-scroll-region="1"]');
      if (parentScroll && parentScroll !== region) return;

      if (!region.getAttribute('role')) region.setAttribute('role', 'region');
      if (!region.getAttribute('aria-label')) {
        const isList = /rev-widg__reviews|rev-widg__body|reviews/i.test(region.className || '');
        region.setAttribute('aria-label', isList ? 'Review list' : 'Review media');
      }
      region.setAttribute('tabindex', '0');
      region.dataset.skJmScrollRegion = '1';
    });
  }

  /**
   * PDP review widget: collapse the long first page to four reviews.
   * Judge.me often ships 10+ tall cards with custom-form rows; community
   * UGC already covers photos above. Expand restores the full list and
   * removes line-clamp so Load more can keep working normally.
   */
  function compactPdpJudgeMeReviews(scopeRoot) {
    if (!document.body.className.includes('template-product')) return;

    const scope = scopeRoot || document;
    const widgets = getNodes(
      scope,
      '#judgeme_product_reviews, .jdgm-review-widget, .jdgm-rev-widg',
    );

    widgets.forEach((widget) => {
      if (widget.dataset.pdpJdgmCompactInit === '1') return;

      const reviews = widget.querySelectorAll('.jdgm-rev, .jdgm-ssr-reviews__list > .jdgm-rev');
      if (reviews.length <= 4) return;

      widget.dataset.pdpJdgmCompactInit = '1';
      widget.classList.add('pdp-jdgm-compact');

      let expand = widget.querySelector('.pdp-jdgm-expand');
      if (!expand) {
        expand = document.createElement('button');
        expand.type = 'button';
        expand.className = 'pdp-jdgm-expand';
        expand.textContent = 'Show more reviews';
        const list =
          widget.querySelector('.jdgm-rev-widg__reviews, .jdgm-ssr-reviews__list, .jdgm-revs') ||
          widget;
        list.after(expand);
      }

      const expandAll = () => {
        widget.classList.remove('pdp-jdgm-compact');
        expand.remove();
      };

      expand.addEventListener('click', expandAll);

      widget.addEventListener(
        'click',
        (event) => {
          const trigger = event.target.closest(
            '.jdgm-paginate__load-more, .jdgm-btn--load-more, [class*="load-more"], a.jdgm-paginate__page',
          );
          if (trigger) expandAll();
        },
        true,
      );
    });
  }

  function enhanceJudgeMeA11y(root) {
    const scope = root || document;

    mountPdpReviewsBrand(scope);
    compactPdpJudgeMeReviews(scope);
    stripHomepageReviewShadowStack(scope);
    layoutHomepageReviewCarousel(scope);
    alignHomepageReviewCardCopy(scope);
    eagerLoadHomepageReviewCarouselImages(scope);
    inertHiddenJudgeMeSlides(scope);
    removeEmptyJudgeMeHeadings(scope);
    lazyLoadJudgeMeImages(scope);
    fixInvalidJudgeMeAriaSelected(scope);
    nameJudgeMeMedalLinks(scope);
    nameJudgeMeHistogramLinks(scope);
    rewriteJudgeMeUsProductReferences(scope);
    makeJudgeMeScrollRegionsKeyboardAccessible(scope);

    {
      /**
       * Cover every nav-button class Judge.me + the Slick/Swiper engines
       * underneath each widget. Older accessibility audits surfaced
       * generic "Review carousel control 1/2/3/4" names because the cards
       * carousel uses `.jdgm-cards-carousel__nav-button` (and Slick's
       * `.slick-prev` / `.slick-next` arrows under the hood) — neither
       * of which were in the previous `.jdgm-arrow` selector. Direction
       * is inferred from the className first, falling back to attribute
       * + label inspection so we still produce "Previous reviews" /
       * "Next reviews" even when the arrow markup carries no prev/next
       * hint.
       */
      const navSelector = [
        '.jdgm-arrow',
        '.jdgm-widget .slick-arrow',
        '.jdgm-widget .slick-prev',
        '.jdgm-widget .slick-next',
        '.jdgm-widget .swiper-button-prev',
        '.jdgm-widget .swiper-button-next',
        '[class*="jdgm-cards-carousel"] button',
        '[class*="jdgm-reviews-carousel"] button',
        '[class*="jdgm-carousel"] button',
      ].join(', ');

      const directionFor = (button) => {
        const className = button.className || '';
        if (/prev|left|previous|backward/i.test(className)) return 'prev';
        if (/next|right|forward/i.test(className)) return 'next';
        const onclick = (button.getAttribute('onclick') || '').toLowerCase();
        if (/jdgmprevious|previouscard|previous/.test(onclick)) return 'prev';
        if (/jdgmnext|nextcard|next/.test(onclick)) return 'next';
        const aria = (button.getAttribute('aria-label') || '').toLowerCase();
        if (/prev|left|previous|backward/.test(aria)) return 'prev';
        if (/next|right|forward/.test(aria)) return 'next';
        const titleAttr = (button.getAttribute('title') || '').toLowerCase();
        if (/prev|left|previous|backward/.test(titleAttr)) return 'prev';
        if (/next|right|forward/.test(titleAttr)) return 'next';
        return null;
      };

      getNodes(scope, '.shopify-section:has(.hp-rv-brand-header) .jdgm-arrows--bottom').forEach((tray) => {
        tray.classList.add('hp-rv-nav');
      });

      getNodes(scope, navSelector).forEach((button, index) => {
        const direction = directionFor(button);
        if (direction === 'prev') button.setAttribute('data-hp-rv-nav', 'prev');
        else if (direction === 'next') button.setAttribute('data-hp-rv-nav', 'next');

        const existing = (button.getAttribute('aria-label') || '').trim();
        const genericLabel =
          !existing ||
          /^review carousel control\b/i.test(existing) ||
          /^reviews navigation\b/i.test(existing) ||
          /^previous slide$/i.test(existing) ||
          /^next slide$/i.test(existing) ||
          /^previous$/i.test(existing) ||
          /^next$/i.test(existing);
        if (!genericLabel) return;

        let label;
        if (direction === 'prev') label = 'Previous reviews';
        else if (direction === 'next') label = 'Next reviews';
        else label = `Reviews navigation ${index + 1}`;
        button.setAttribute('aria-label', label);
      });
    }

    getNodes(scope, '.jdgm-widget .jdgm-rev-snippet-widget__prev-btn, .jdgm-widget .jdgm-rev-snippet-widget__next-btn').forEach(
      (icon, index) => {
        const className = icon.className || '';
        const isPrev = /prev|left/i.test(className);
        const isNext = /next|right/i.test(className);
        const label = isPrev
          ? 'Previous reviews'
          : isNext
            ? 'Next reviews'
            : `Review snippet control ${index + 1}`;
        icon.setAttribute('role', 'button');
        icon.setAttribute('tabindex', '0');
        if (!icon.getAttribute('aria-label')) icon.setAttribute('aria-label', label);
      },
    );

    getNodes(scope, '.jdgm-rev-snippet-card__full-review-link').forEach((link, index) => {
      if (!link.getAttribute('aria-label')) {
        const card = link.closest('.jdgm-rev-snippet-card');
        const reviewer =
          (card?.querySelector('.jdgm-rev__author')?.textContent || '').trim() ||
          (card?.querySelector('.jdgm-rev-snippet-card__author')?.textContent || '').trim();
        link.setAttribute(
          'aria-label',
          reviewer ? `Read full review by ${reviewer}` : `Read full review ${index + 1}`,
        );
      }
    });

    getNodes(scope, '.jdgm-widget img').forEach((img, index) => {
      const altNow = (img.getAttribute('alt') || '').trim();
      const genericAlt =
        !altNow ||
        altNow === 'Customer review media' ||
        /^review media from\b/i.test(altNow) ||
        altNow.toLowerCase() === 'review media';
      if (!genericAlt) return;

      const card = img.closest(
        '.jdgm-card, .jdgm-rev-snippet-card, .jdgm-reviews-carousel__item, [class*="jdgm-rev"]',
      );
      const name =
        (card?.querySelector('.jdgm-rev__author, .jdgm-rev-snippet-card__author, .jdgm-reviewer__name')?.textContent || '')
          .trim()
          .replace(/\s+/g, ' ') || '';
      const bodyEl = card?.querySelector(
        '.jdgm-rev__body, .jdgm-rev-snippet-card__review-body, .jdgm-rev__content, .jdgm-rev-snippet-card__review',
      );
      let snippet = (bodyEl?.textContent || '').trim().replace(/\s+/g, ' ');
      if (snippet.length > 96) snippet = `${snippet.slice(0, 93)}…`;

      let nextAlt = '';
      if (name && snippet) {
        const short = snippet.length > 72 ? `${snippet.slice(0, 72)}…` : snippet;
        nextAlt = `Customer photo in ${name}'s review: ${short}`;
      } else if (name) {
        const cardImgs = card ? Array.from(card.querySelectorAll('img')) : [];
        const mediaNum = Math.max(1, cardImgs.indexOf(img) + 1);
        nextAlt =
          cardImgs.length > 1 ? `Photo ${mediaNum} from ${name}'s review` : `Photo from ${name}'s review`;
      } else if (snippet) {
        const short = snippet.length > 72 ? `${snippet.slice(0, 72)}…` : snippet;
        nextAlt = `Customer review photo: ${short}`;
      } else {
        nextAlt = `Customer review photo ${index + 1}`;
      }

      if (nextAlt.length > 155) nextAlt = `${nextAlt.slice(0, 152)}…`;
      img.setAttribute('alt', nextAlt);
    });
  }

  const run = () => enhanceJudgeMeA11y(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }

  let attrPassTimer = 0;
  const observer = new MutationObserver((mutations) => {
    let needsAttrPass = false;
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        needsAttrPass = true;
        continue;
      }
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) enhanceJudgeMeA11y(node);
      });
    }
    if (!needsAttrPass) return;
    window.clearTimeout(attrPassTimer);
    attrPassTimer = window.setTimeout(() => {
      fixInvalidJudgeMeAriaSelected(document);
      makeJudgeMeScrollRegionsKeyboardAccessible(document);
      nameJudgeMeHistogramLinks(document);
      if (document.body.classList.contains('us-showcase')) {
        rewriteJudgeMeUsProductReferences(document);
      }
    }, 40);
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-selected', 'class', 'style'],
  });

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      layoutHomepageReviewCarousel(document);
      alignHomepageReviewCardCopy(document);
      makeJudgeMeScrollRegionsKeyboardAccessible(document);
    }, 150);
  });

  /* Arrow-key support for media-grid radios after we convert the pattern. */
  document.addEventListener(
    'keydown',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.getAttribute('role') !== 'radio') return;
      const group = target.closest('[role="radiogroup"]');
      if (!group || !group.matches('.jm-media-grid, .jdgm-gallery, [class*="jm-media-grid"]')) return;

      const radios = Array.from(group.querySelectorAll('[role="radio"]'));
      const index = radios.indexOf(target);
      if (index < 0) return;

      let next = -1;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % radios.length;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + radios.length) % radios.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = radios.length - 1;
      if (next < 0) return;

      event.preventDefault();
      radios[next].focus();
      radios[next].click();
    },
    true,
  );
})();
